import { MutableRefObject, useMemo, useCallback } from 'react';
import type { Settings } from 'handsontable/plugins/contextMenu/contextMenu';

// Types
import { TextAlignment, VerticalAlignment } from '../../../../../common/table/CellTypeConstants';
import { isGlobalDebugEnabled } from '../../../../debug/debugUtil';
import { debugTableCell, debugTableInstance } from '../../utils/hotInstanceUtils/debug/hotTableDebugUtils';
import { capitalize } from 'lodash';
import {
    isHotTableTextAlignmentApplied,
    isHotTableVerticalAlignmentApplied,
} from '../../utils/hotInstanceUtils/cell/hotTableAlignmentUtils';
import {
    updateHotTableSelectedCellTextAlignment,
    updateHotTableSelectedCellVerticalAlignment,
} from '../../utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';
import {
    addHotTableColsLeft,
    addHotTableColsRight,
    addHotTableRowsAbove,
    addHotTableRowsBelow,
    isHotTableColRemovalDisabled,
    isHotTableRowRemovalDisabled,
    removeHotTableCols,
    removeHotTableRows,
} from '../../utils/hotInstanceUtils/grid/hotTableGridOperationsUtils';
import {
    isSelectingSingleColumn,
    isSelectingSingleRow,
} from '../../utils/selectionUtils/tableSelectionClassificationUtils';
import { isSelectingMultipleSelections } from '../../utils/selectionUtils/tableSelectionPredicateUtils';
import { getContextMenuShortcutHtml } from './tableContextMenuShortcutUtils';
import {
    copyTableCellsFromContextMenu,
    cutTableCellsFromContextMenu,
    pasteTableCellsFromContextMenu,
} from './tableContextMenuClipboardUtils';
import { useTranslator } from '../../../../components/i18n/useTranslator';
import { MNHotInstance } from '../../tableHotInstanceTypes';

const SEPARATOR = '---------';

type TableContextMenuTranslationFunction = (translationKey: string, fallback: string) => string;

interface UseTableContextMenuProps {
    elementId: string;
    locale: string;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
}

export interface UseTableContextMenuReturn {
    getTableContextMenuConfig: Settings;
}

const useTableContextMenu = ({
    elementId,
    locale,
    hotTableInstanceRef,
}: UseTableContextMenuProps): UseTableContextMenuReturn => {
    const translateTableContextMenu = useTranslator(
        'canvas',
        'contextMenuItems.table',
    ) as TableContextMenuTranslationFunction;

    /**
     * Reads the latest HOT selection when menu labels or disabled state need current selection semantics.
     */
    const getCurrentSelections = useCallback(() => hotTableInstanceRef.current?.getSelected(), [hotTableInstanceRef]);

    const dispatchUpdateTableCellTextAlignment = useCallback(
        (textAlignment) => () => {
            if (!hotTableInstanceRef.current) return;

            return updateHotTableSelectedCellTextAlignment(hotTableInstanceRef.current, textAlignment);
        },
        [hotTableInstanceRef],
    );

    const dispatchUpdateTableCellVerticalAlignment = useCallback(
        (verticalAlignment) => () => {
            if (!hotTableInstanceRef.current) return;

            updateHotTableSelectedCellVerticalAlignment(hotTableInstanceRef.current, verticalAlignment);
        },
        [hotTableInstanceRef],
    );

    const getTableContextMenuConfig = useMemo((): Settings => {
        const getAddColsLeftMenuName = () => {
            const useSingleColumnLabel = isSelectingSingleColumn(getCurrentSelections());

            const labelSingle = translateTableContextMenu('addColumnLeft_one', 'Insert column left');
            const labelPlural = translateTableContextMenu('addColumnLeft_other', 'Insert columns left');
            const label = useSingleColumnLabel ? labelSingle : labelPlural;
            return `${label} ${getContextMenuShortcutHtml(['alt', '←'])}`;
        };

        const getAddColsRightMenuName = () => {
            const useSingleColumnLabel = isSelectingSingleColumn(getCurrentSelections());

            const labelSingle = translateTableContextMenu('addColumnRight_one', 'Insert column right');
            const labelPlural = translateTableContextMenu('addColumnRight_other', 'Insert columns right');
            const label = useSingleColumnLabel ? labelSingle : labelPlural;
            return `${label} ${getContextMenuShortcutHtml(['alt', '→'])}`;
        };

        const getRemoveColsMenuName = () => {
            const useSingleColumnLabel = isSelectingSingleColumn(getCurrentSelections());

            const labelSingle = translateTableContextMenu('removeColumn_one', 'Remove column');
            const labelPlural = translateTableContextMenu('removeColumn_other', 'Remove columns');
            return useSingleColumnLabel ? labelSingle : labelPlural;
        };

        const getAddRowsAboveMenuName = () => {
            const useSingleRowLabel = isSelectingSingleRow(getCurrentSelections());

            const labelSingle = translateTableContextMenu('addRowAbove_one', 'Insert row above');
            const labelPlural = translateTableContextMenu('addRowAbove_other', 'Insert rows above');
            const label = useSingleRowLabel ? labelSingle : labelPlural;
            return `${label} ${getContextMenuShortcutHtml(['alt', '↑'])}`;
        };

        const getAddRowsBelowMenuName = () => {
            const useSingleRowLabel = isSelectingSingleRow(getCurrentSelections());

            const labelSingle = translateTableContextMenu('addRowBelow_one', 'Insert row below');
            const labelPlural = translateTableContextMenu('addRowBelow_other', 'Insert rows below');
            const label = useSingleRowLabel ? labelSingle : labelPlural;
            return `${label} ${getContextMenuShortcutHtml(['alt', '↓'])}`;
        };

        const getRemoveRowsMenuName = () => {
            const useSingleRowLabel = isSelectingSingleRow(getCurrentSelections());

            const labelSingle = translateTableContextMenu('removeRow_one', 'Remove row');
            const labelPlural = translateTableContextMenu('removeRow_other', 'Remove rows');
            return useSingleRowLabel ? labelSingle : labelPlural;
        };

        const getTextAlignmentMenuName = (textAlignment: TextAlignment) => () => {
            if (!hotTableInstanceRef.current) return '';

            const isTextAlignmentApplied = isHotTableTextAlignmentApplied(
                hotTableInstanceRef.current,
                textAlignment,
                locale,
            );

            const label = translateTableContextMenu(`align${capitalize(textAlignment)}`, `Align ${textAlignment}`);
            return `${label}${isTextAlignmentApplied ? '<span class="tick">✔︎</span>' : ''}`;
        };

        const getVerticalAlignmentMenuName = (verticalAlignment: VerticalAlignment) => () => {
            if (!hotTableInstanceRef.current) return '';

            const isVerticalAlignmentApplied = isHotTableVerticalAlignmentApplied(
                hotTableInstanceRef.current,
                verticalAlignment,
            );

            const label = translateTableContextMenu(
                `align${capitalize(verticalAlignment)}`,
                `Align ${verticalAlignment}`,
            );
            return `${label}${isVerticalAlignmentApplied ? '<span class="tick">✔︎</span>' : ''}`;
        };

        const isTableSelectingMultipleSelections = () => isSelectingMultipleSelections(getCurrentSelections());

        return {
            items: {
                cut: {
                    name: () => translateTableContextMenu('cut', 'Cut'),
                    callback: () => cutTableCellsFromContextMenu(hotTableInstanceRef.current),
                },
                copy: {
                    name: () => translateTableContextMenu('copy', 'Copy'),
                    callback: () => copyTableCellsFromContextMenu(hotTableInstanceRef.current),
                },
                paste: {
                    key: 'paste',
                    name: () => translateTableContextMenu('paste', 'Paste'),
                    callback: () => void pasteTableCellsFromContextMenu(hotTableInstanceRef.current),
                },
                separator4: { name: SEPARATOR },
                add_rows_above_custom: {
                    name: getAddRowsAboveMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableRowsAbove(hotTableInstanceRef.current, undefined),
                },
                add_rows_below_custom: {
                    name: getAddRowsBelowMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableRowsBelow(hotTableInstanceRef.current, undefined),
                },
                separator1: { name: SEPARATOR },
                add_cols_left_custom: {
                    name: getAddColsLeftMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableColsLeft(hotTableInstanceRef.current, undefined),
                },
                add_cols_right_custom: {
                    name: getAddColsRightMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableColsRight(hotTableInstanceRef.current, undefined),
                },
                separator2: { name: SEPARATOR },
                remove_rows_custom: {
                    name: getRemoveRowsMenuName,
                    disabled: () => isHotTableRowRemovalDisabled(hotTableInstanceRef.current),
                    callback: () => removeHotTableRows(hotTableInstanceRef.current, undefined),
                },
                remove_cols_custom: {
                    name: getRemoveColsMenuName,
                    disabled: () => isHotTableColRemovalDisabled(hotTableInstanceRef.current),
                    callback: () => removeHotTableCols(hotTableInstanceRef.current, undefined),
                },
                separator3: { name: SEPARATOR },
                custom_alignment: {
                    name: () => translateTableContextMenu('changeAlignment', 'Change Alignment...'),
                    submenu: {
                        items: [
                            {
                                key: 'custom_alignment:left',
                                name: getTextAlignmentMenuName(TextAlignment.LEFT),
                                callback: dispatchUpdateTableCellTextAlignment(TextAlignment.LEFT),
                            },
                            {
                                key: 'custom_alignment:center',
                                name: getTextAlignmentMenuName(TextAlignment.CENTER),
                                callback: dispatchUpdateTableCellTextAlignment(TextAlignment.CENTER),
                            },
                            {
                                key: 'custom_alignment:right',
                                name: getTextAlignmentMenuName(TextAlignment.RIGHT),
                                callback: dispatchUpdateTableCellTextAlignment(TextAlignment.RIGHT),
                            },
                            { key: 'separator', name: SEPARATOR },
                            {
                                key: 'custom_alignment:top',
                                name: getVerticalAlignmentMenuName(VerticalAlignment.TOP),
                                callback: dispatchUpdateTableCellVerticalAlignment(VerticalAlignment.TOP),
                            },
                            {
                                key: 'custom_alignment:middle',
                                name: getVerticalAlignmentMenuName(VerticalAlignment.MIDDLE),
                                callback: dispatchUpdateTableCellVerticalAlignment(VerticalAlignment.MIDDLE),
                            },
                            {
                                key: 'custom_alignment:bottom',
                                name: getVerticalAlignmentMenuName(VerticalAlignment.BOTTOM),
                                callback: dispatchUpdateTableCellVerticalAlignment(VerticalAlignment.BOTTOM),
                            },
                        ],
                    },
                },
                ...(isGlobalDebugEnabled()
                    ? {
                          separator5: { name: SEPARATOR },

                          debugTableInstance: {
                              name: () => 'Debug Table Instance',
                              callback: debugTableInstance,
                              hidden: !isGlobalDebugEnabled(),
                          },
                          debugCell: {
                              name: () => 'Debug Cell',
                              callback: debugTableCell(elementId),
                              hidden: !isGlobalDebugEnabled(),
                          },
                      }
                    : {}),
            },
        };
        // Depend on the session-bound callbacks so memoized menu actions always target the latest active session.
    }, [
        elementId,
        locale,
        hotTableInstanceRef,
        translateTableContextMenu,
        dispatchUpdateTableCellTextAlignment,
        dispatchUpdateTableCellVerticalAlignment,
        getCurrentSelections,
    ]);

    return {
        getTableContextMenuConfig,
    };
};

export default useTableContextMenu;
